import { useMemo } from "react";
import type { Money, ModelRate } from "@empite/api-client";
import { formatMoney } from "@empite/api-client";
import { useModelRates } from "../billing";
import { Card, CardBody, CardHeader } from "../ui/card";
import { DataTable } from "../ui/data-table";
import type { Column } from "../ui/data-table";
import { EmptyState } from "../ui/empty-state";
import { formatDate } from "../ui/format";
import { Notice } from "../ui/notice";
import { Skeleton } from "../ui/skeleton";
import { StatusPill } from "../ui/status-pill";
import { errorMessage } from "./guards";

/** One table row. DataTable keys on `id`, and an alias is unique in a list
 *  that is one profile's allow-list, so the alias IS the id. */
interface RateRow {
  id: string;
  rate: ModelRate;
}

/** The five dimensions, under the words a customer uses for them. The server
 *  sends them in the fixed order `Usage.dimensions` returns and this map only
 *  supplies labels — it must never reorder or filter, or a card and an invoice
 *  line would stop lining up. */
const DIMENSION_LABELS: Record<string, string> = {
  input: "Input",
  cached_input: "Cached input",
  cache_write: "Cache write",
  output: "Output",
  reasoning: "Reasoning",
};

/**
 * A money amount printed at full rate precision.
 *
 * `formatMoney` truncates to the currency's minor unit, which is right for a
 * balance and wrong for a rate: a per-request fee of USD 0.000015 would print
 * as "USD 0.00". The wire's `display` is the amount rendered to six decimals
 * with trailing zeros trimmed back to the currency exponent, which is exactly
 * a rate. It is used for DISPLAY only — nothing here parses it — so the
 * "never authoritative" rule on that field is respected.
 */
function rateLabel(amount: Money): string {
  return amount.display ? `${amount.currency} ${amount.display}` : formatMoney(amount);
}

/**
 * What each model costs, for one application's wallet.
 *
 * Every number on this card arrived priced. The server ran the SAME pricing
 * code that charges a request (internal/billing/pricing.go) over the in-force
 * provider price version and this application's markup plan, so the rate here
 * and the rate on the invoice cannot disagree. Nothing in this file multiplies
 * anything: no markup is applied in TypeScript, and a future edit that starts
 * to would be reintroducing the one defect a pricing page must not have.
 *
 * A model with no price in force is not in the response at all. That is the
 * server's rule and the screen does not soften it with a "price unavailable"
 * row: a model this platform cannot quote is one it would refuse at reserve,
 * and offering it here would only move the disappointment later.
 */
export function ModelRatesCard({ applicationID, name }: { applicationID: string; name: string }) {
  const rates = useModelRates(applicationID);
  const rows = useMemo<RateRow[]>(
    () => (rates.data?.models ?? []).map((rate) => ({ id: rate.model, rate })),
    [rates.data],
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Model rates"
        description="What this application pays per million tokens, with your plan applied."
      />
      {rates.error ? (
        <CardBody>
          <Notice tone="danger" title="We could not load your model rates">
            {errorMessage(rates.error)}
          </Notice>
        </CardBody>
      ) : rates.isPending ? (
        <CardBody>
          <Skeleton className="h-24 w-full" />
          <span className="sr-only">Loading model rates</span>
        </CardBody>
      ) : (
        <DataTable
          caption={`Model rates for ${name}`}
          columns={rateColumns}
          rows={rows}
          empty={
            <EmptyState
              size="sm"
              title="No models priced yet"
              body="Models appear here once an operator has published a price for them."
            />
          }
        />
      )}
    </Card>
  );
}

const rateColumns: Column<RateRow>[] = [
  {
    key: "model",
    header: "Model",
    cell: (row) => (
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-ink">{row.rate.display_name || row.rate.model}</span>
          {row.rate.deprecated ? <StatusPill tone="warning">Deprecated</StatusPill> : null}
        </div>
        <code className="font-mono text-xs text-ink-4">{row.rate.model}</code>
        {row.rate.deprecated && row.rate.replacement_alias ? (
          <p className="text-xs text-ink-4">Use {row.rate.replacement_alias} instead.</p>
        ) : null}
      </div>
    ),
  },
  {
    key: "providers",
    header: "Served by",
    className: "hidden lg:table-cell",
    cell: (row) => <span className="text-sm text-ink-3">{row.rate.providers.join(" → ")}</span>,
  },
  {
    key: "rates",
    header: "Per million tokens",
    align: "right",
    cell: (row) => (
      <dl className="ml-auto grid w-max grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {row.rate.pricing.rates.map((line) => (
          <div key={line.dimension} className="contents">
            <dt className="text-left text-ink-4">
              {DIMENSION_LABELS[line.dimension] ?? line.dimension}
            </dt>
            <dd className="tnum text-right whitespace-nowrap text-ink">{rateLabel(line.per_million)}</dd>
          </div>
        ))}
      </dl>
    ),
  },
  {
    key: "per_request",
    header: "Per request",
    align: "right",
    // The fee and the floor are per REQUEST and the column beside them is per
    // token; they are kept apart rather than folded in because a card that
    // added a flat fee into a rate could not be read back out.
    cell: (row) => (
      <div className="space-y-1 text-sm">
        <div className="tnum whitespace-nowrap text-ink">{rateLabel(row.rate.pricing.request_fee)}</div>
        {row.rate.pricing.minimum_charge.micros !== "0" ? (
          <div className="text-xs text-ink-4">
            min {rateLabel(row.rate.pricing.minimum_charge)}
          </div>
        ) : null}
      </div>
    ),
  },
  {
    key: "since",
    header: "In force since",
    align: "right",
    className: "hidden xl:table-cell",
    cell: (row) => (
      <span className="text-xs text-ink-4">{formatDate(row.rate.pricing.effective_from)}</span>
    ),
  },
];
